import React, { useState, useEffect } from 'react';
import axios from '../common/axios';
import { Dialog } from '@progress/kendo-react-dialogs';
import { Form, FormElement, Field, FieldWrapper } from '@progress/kendo-react-form';
import { Error, Hint, Label } from '@progress/kendo-react-labels';
import { Upload, ExternalDropZone } from '@progress/kendo-react-upload';
import { Button } from '@progress/kendo-react-buttons';
import { IntlProvider, LocalizationProvider, loadMessages } from "@progress/kendo-react-intl";
import krMessage2 from '../login/multistep/kr.json';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';

loadMessages(krMessage2, "ko-KR");

const uploadRef = React.createRef();

const FormUpload = fieldRenderProps => {
  const {
    value,
    id,
    optional,
    label,
    hint,
    validationMessage,
    touched,
    ...others
  } = fieldRenderProps;
  
  const showValidationMessage = touched && validationMessage;
  const showHint = !showValidationMessage && hint;
  const hintId = showHint ? `${id}_hint` : '';
  const errorId = showValidationMessage ? `${id}_error` : '';
  const labelId = label ? `${id}_label` : '';
  const onChangeHandler = event => {
    fieldRenderProps.onChange({
      value: event.newState
    });
  };
  const onRemoveHandler = event => {
    fieldRenderProps.onChange({
      value: event.newState
    });
  };

  const display = fieldRenderProps.value && fieldRenderProps.value.length > 0 ? "none" : 'block';

  return (
    <FieldWrapper>
      <Label id={labelId} editorId={id} optional={optional} className='k-form-label'>
        {label}
      </Label>
      <LocalizationProvider language="ko-KR">
        <IntlProvider locale="kr">
          <div className={'k-form-field-wrap'}>
            <Upload ref={uploadRef}
              restrictions={{
                allowedExtensions: [".pdf"],
              }}
              id={id} autoUpload={false} showActionButtons={false} multiple={true} files={value} onAdd={onChangeHandler} onRemove={onRemoveHandler} ariaDescribedBy={`${hintId} ${errorId}`} ariaLabelledBy={labelId} {...others} />
            {showHint && <Hint id={hintId}>{hint}</Hint>}
            {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
          </div>
          <div style={{ height: "10px" }}></div>
          <div style={{ display: display }}>
            <ExternalDropZone uploadRef={uploadRef} />
          </div>
        </IntlProvider>
      </LocalizationProvider>
    </FieldWrapper>
  );
};

const BondPage = () => {
  const [data, setData] = useState([]);
  const [visibleDialog, setVisibleDialog] = useState(false);
  const [currentBond, setCurrentBond] = useState(null);
  const { t } = useTranslation();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get('/getbondList');
        if (res.data.success) {
          setData(res.data.data);
        }
      } catch (error) {
        console.error('데이터 가져오기 중 오류 발생:', error);
      }
    };
    fetchData();
  }, []);

  const handleButtonClick = (e) => {
    setVisibleDialog(true);
    setCurrentBond(e);
  };

  const handleCloseModal = () => {
    setVisibleDialog(false);
    setCurrentBond(null);
  };

  const onStepSubmit = React.useCallback(event => {
    const affectedFiles = event.values[currentBond.record];
    const updatedState = [];

    affectedFiles.forEach((file) => {
      const cc = file.getRawFile();
      const reader = new FileReader();
      reader.onload = function (loadEvent) {
        const base64 = loadEvent.target.result;
        const updatedFile = {
          name: file.name,
          base64: base64,
        };
        updatedState.push(updatedFile);

        if (updatedState.length === affectedFiles.length) {
          handleCloseModal();
          Swal.fire({
            title: t('bondLog.confirmUpload'),
            text: t('bondLog.confirmUpload'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('bondLog.uploadButton'),
            cancelButtonText: '아니오'
          }).then((result) => {
            if (result.isConfirmed) {
              sendInfo(updatedState);
            }
          });
        }
      };
      reader.readAsDataURL(cc);
    });
  });

  const sendInfo = (e) => {
    axios.post("/updatewithattach", {
      bpname: "SHI Contract",
      project: currentBond.projectCode,
      record: currentBond.record,
      files: e
    })
    .then(response => {
      if (response.data.success) {
        Swal.fire({
          title: t('bondLog.uploadSuccess'),
          text: t('bondLog.uploadSuccess'),
          icon: 'success',
          confirmButtonText: 'OK'
        }).then(() => {
          window.location.reload();
        });
      } else {
        let errorMessage = response.data.error || t('bondLog.uploadError');
        Swal.fire({
          title: 'Error',
          text: errorMessage,
          icon: 'error',
          confirmButtonText: 'OK'
        });
      }
    })
    .catch(err => {
      let errorMessage = t('bondLog.uploadError');
      if (err.response && err.response.data && err.response.data.error) {
        errorMessage += ' ' + err.response.data.error;
      }
      Swal.fire({
        title: 'Error',
        text: errorMessage,
        icon: 'error',
        confirmButtonText: 'OK'
      });
    });
  };

  return (
    <>
      <div className='app-content flex-column-fluid'>
        <div className='app-container container-xxl'>
          <div className="card mb-5 mb-xl-8">
            <div className="card-header border-0 pt-5">
              <h3 className="card-title align-items-start flex-column">
                <span className="card-label fw-bold fs-3 mb-1">{t('nav.guaranteeInsuranceUpload')}</span>
                <span className="text-muted mt-1 fw-semibold fs-7">
  {t('bondLog.uploadRequired', { count: data.length })}
</span>
              </h3>
              <div className="card-toolbar"></div>
            </div>
            <div className="card-body py-3">
              <div className="table-responsive">
                <table className="table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3">
                  <thead>
                    <tr className="fw-bold text-muted">
                      <th className="min-w-100px">{t('bondLog.projectName')}</th>
                      <th className="min-w-250px">{t('bondLog.contractName')}</th>
                      <th className="min-w-150px">{t('bondLog.contractAmount')}</th>
                      <th className="min-w-120px">{t('bondLog.advanceAmount')}</th>
                      <th className="min-w-200px">{t('bondLog.requiredGuaranteeList')}</th>
                      <th className="min-w-200px">{t('bondLog.uploadedFileList')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data && data.map((v, index) => (
                      <tr key={index}>
                        <td className="text-dark fw-bold text-hover-primary fs-6">{v.프로젝트}</td>
                        <td className="text-dark fw-bold text-hover-primary fs-6">{v.계약명}</td>
                        <td className="text-dark fw-bold text-hover-primary fs-6">{v.계약금_공급}</td>
                        <td className="text-dark fw-bold text-hover-primary fs-6">{v.선급금_공급}</td>
                        <td>
                          {v["보증필요리스트"].map((vf, index) => (
                            <span key={index} className="text-dark text-hover-primary fs-7 d-block">{vf}</span>
                          ))}
                        </td>
                        <td>
                          {v["파일 리스트"].length === 0 ? <span key={index} className="text-dark text-hover-danger fs-7 d-block">{t('bondLog.noUploadedFiles')}</span> : v["파일 리스트"].map((vf, index) => (
                            <span key={index} className="text-dark text-hover-primary fs-7 d-block">{vf}</span>
                          ))}
                        </td>
                        <td className="text-end">
                          <span onClick={(e) => { e.preventDefault(); handleButtonClick(v); }} className="btn btn-sm btn-icon btn-bg-light btn-active-color-primary">
                            <i className="ki-duotone ki-folder-up ">
                              <span className="path1"></span>
                              <span className="path2"></span>
                            </i>
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      {visibleDialog &&
        <Dialog title={currentBond.계약명 + " " + t('bondLog.uploadHint')} width={600} onClose={handleCloseModal}>
          <Form className="my-auto pb-5" onSubmitClick={onStepSubmit} render={formRenderProps => <div>
            {/* 스텝 바디 */}
            <FormElement>
              <Field
                key={currentBond.record} className="attach"
                id={currentBond.record}
                name={currentBond.record}
                label={t('bondLog.uploadHint')}
                hint={t('bondLog.uploadHint')}
                component={FormUpload}
              />
              <div className="d-flex flex-stack pt-15">
                <div>
                  <Button type="button" className="btn btn-lg btn-primary" disabled={!formRenderProps.allowSubmit} onClick={formRenderProps.onSubmit}>
                    {t('bondLog.uploadButton')}
                    <i className="ki-duotone ki-arrow-right fs-4 ms-1">
                      <span className="path1"></span>
                      <span className="path2"></span>
                    </i>
                  </Button>
                </div>
              </div>
            </FormElement>
          </div>} />
        </Dialog>
      }
    </>
  );
};

export default BondPage;
